import axios from 'axios';
import { ADMIN_VIEW_HEADER, getAdminViewRegistrant } from './adminView';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://127.0.0.1:5000/api';

const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add auth headers to requests
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  // Telemetry endpoints also use X-Registrant-ID as fallback auth
  const registrantId = localStorage.getItem('registrant_id');
  if (registrantId) {
    config.headers['X-Registrant-ID'] = String(registrantId);
  }
  // An administrator viewing a specific registrant's data.
  //
  // Deliberately a different header from X-Registrant-ID above. That one is an
  // unauthenticated assertion the server honours only outside deployed
  // environments; this one is honoured in production and therefore has to prove
  // who is asking. The server ignores it for anybody who is not an
  // administrator, and on every method except GET and HEAD, so it can never
  // carry a write to another account. See utils/telemetry_auth.py.
  const viewing = getAdminViewRegistrant();
  if (viewing) {
    config.headers[ADMIN_VIEW_HEADER] = viewing;
  }
  return config;
});

// Handle 401 errors
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response && error.response.status === 401) {
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      window.location = '/login';
    }
    return Promise.reject(error);
  }
);

// ==================== V2 HIGH-PERFORMANCE API ====================
// Optimized endpoints for millions of records with server-side pagination

/**
 * Fetch paginated energy readings with server-side filtering
 * @param {Object} params - Query parameters
 * @param {number} params.page - Page number (1-indexed)
 * @param {number} params.page_size - Items per page (max 100)
 * @param {string} params.device_id - Filter by device
 * @param {string} params.facility_id - Filter by facility
 * @param {string} params.start_date - Filter from date (YYYY-MM-DD)
 * @param {string} params.end_date - Filter to date (YYYY-MM-DD)
 * @param {string} params.status - Filter by validation_status
 * @param {string} params.sort_by - Sort field (timestamp, kwh_generated, device_id)
 * @param {string} params.sort_order - asc or desc
 */
export const getEnergyReadingsV2 = async (params = {}) => {
  const response = await api.get('/v2/energy/readings', { params });
  return response.data;
};

/**
 * Get energy summary stats (fast dashboard data)
 */
export const getEnergySummaryV2 = async () => {
  const response = await api.get('/v2/energy/summary');
  return response.data;
};

/**
 * Fetch paginated REC pipeline batches
 * @param {Object} params - Query parameters
 * @param {number} params.page - Page number
 * @param {number} params.page_size - Items per page
 * @param {string} params.status - Filter by status
 * @param {string} params.sort_by - Sort field
 * @param {string} params.sort_order - asc or desc
 */
export const getPipelineBatchesV2 = async (params = {}) => {
  const response = await api.get('/v2/pipeline/batches', { params });
  return response.data;
};

/**
 * Get pipeline summary stats
 */
export const getPipelineSummaryV2 = async () => {
  const response = await api.get('/v2/pipeline/summary');
  return response.data;
};

/**
 * Fetch paginated issuance status
 * @param {Object} params - Query parameters
 */
export const getIssuanceStatusV2 = async (params = {}) => {
  const response = await api.get('/v2/issuance/status', { params });
  return response.data;
};

/**
 * Get issuance summary stats
 */
export const getIssuanceSummaryV2 = async () => {
  const response = await api.get('/v2/issuance/summary');
  return response.data;
};

/**
 * Get all dashboard stats in one call
 */
export const getDashboardOverviewV2 = async () => {
  const response = await api.get('/v2/dashboard/overview');
  return response.data;
};

/**
 * Get cache statistics (for monitoring)
 */
export const getCacheStatsV2 = async () => {
  const response = await api.get('/v2/cache/stats');
  return response.data;
};

/**
 * Invalidate cache for current user
 */
export const invalidateCacheV2 = async () => {
  const response = await api.post('/v2/cache/invalidate');
  return response.data;
};

export default api;
